import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import WorldClassLeaderboard from '../components/WorldClassLeaderboard';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  Calendar,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Award,
  Package,
  Users,
  Zap,
  Trophy,
  Medal,
  Crown,
  Star,
  ChevronUp,
  ChevronDown,
  Sparkles,
  TrendingUp as TrendUp
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Line,
  ComposedChart,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar
} from 'recharts';

const DashboardPage = () => {
  const { user, api, isAdmin, isManager } = useAuth();
  const [stats, setStats] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [offerStats, setOfferStats] = useState([]);
  const [dateWiseSales, setDateWiseSales] = useState([]);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [leaderboardMeta, setLeaderboardMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null); // { year, month }
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);

  const COLORS = useMemo(() => ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#10b981', '#3b82f6'], []);

  // Fetch available months on mount
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const res = await api.get('/dashboard/available-months');
        setAvailableMonths(res.data);
        // Set current month as default
        const currentMonth = res.data.find(m => m.is_current);
        if (currentMonth) {
          setSelectedMonth({ year: currentMonth.year, month: currentMonth.month });
        } else if (res.data.length > 0) {
          setSelectedMonth({ year: res.data[0].year, month: res.data[0].month });
        }
      } catch (error) {
        console.error('Error fetching months:', error);
      }
    };
    fetchMonths();
  }, [api]);

  const fetchDashboardData = useCallback(async () => {
    if (!selectedMonth) return;
    
    try {
      setLoading(true);
      const { year, month } = selectedMonth;
      
      const [statsRes, myStatsRes, offerRes, dateWiseRes] = await Promise.all([
        api.get(`/dashboard/stats-by-month?year=${year}&month=${month}`),
        api.get('/dashboard/my-sales-stats'),
        api.get(`/dashboard/offer-stats-by-month?year=${year}&month=${month}`),
        api.get(`/dashboard/date-wise-sales-by-month?year=${year}&month=${month}`),
      ]);

      setStats(statsRes.data);
      setMyStats(myStatsRes.data);
      setOfferStats(offerRes.data || []);
      setDateWiseSales(dateWiseRes.data || []);

      // Fetch leaderboard for selected month
      try {
        const leaderboardRes = await api.get(`/dashboard/leaderboard-by-month?year=${year}&month=${month}`);
        setTeamPerformance(leaderboardRes.data);
        setLeaderboardMeta({
          month_period: statsRes.data.month_period,
          working_days: statsRes.data.working_days
        });
      } catch (e) {
        console.log('Leaderboard not available');
      }
    } catch (error) {
      console.error('Dashboard error:', error);
      setStats({
        total_sales: 0,
        selected_month_sales: 0,
        previous_month_sales: 0,
        growth_percentage: 0,
        daily_average: 0,
        active_agents: 0,
        month_period: ''
      });
      setMyStats({
        my_total_sales: 0,
        my_current_month_sales: 0,
        my_growth_percentage: 0,
        my_daily_average: 0,
        month_period: ''
      });
      setOfferStats([]);
      setDateWiseSales([]);
    } finally {
      setLoading(false);
    }
  }, [api, selectedMonth]);

  const handleMonthSelect = (monthData) => {
    setSelectedMonth({ year: monthData.year, month: monthData.month });
    setShowMonthDropdown(false);
  };

  useEffect(() => {
    if (selectedMonth) {
      fetchDashboardData();
    }
  }, [selectedMonth, fetchDashboardData]);

  const StatCard = ({ title, value, icon: Icon, trend, subtitle, variant = 'default', prefix = '' }) => {
    const variants = {
      default: 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800',
      indigo: 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/25',
      emerald: 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/25',
      sky: 'bg-sky-600 text-white shadow-lg shadow-sky-600/25',
      amber: 'bg-amber-500 text-white shadow-lg shadow-amber-500/25',
    };
    
    const isAccent = variant !== 'default';
    
    return (
      <div className={`rounded-xl p-5 ${variants[variant]} transition-all duration-200 hover:shadow-md`}>
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAccent ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
            <Icon className={`w-5 h-5 ${isAccent ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`} />
          </div>
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              trend >= 0 
                ? isAccent ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                : isAccent ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
            }`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend).toFixed(1)}%
            </div>
          )}
        </div>
        <p className={`text-2xl font-bold mb-1 tabular-nums ${isAccent ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
          {prefix}{typeof value === 'number' ? value.toLocaleString() : value || 0}
        </p>
        <p className={`text-sm font-medium ${isAccent ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>{title}</p>
        {subtitle && (
          <p className={`text-xs mt-1 ${isAccent ? 'text-white/60' : 'text-slate-400 dark:text-slate-500'}`}>{subtitle}</p>
        )}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg">
          <p className="text-slate-900 dark:text-white font-semibold text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-slate-500 dark:text-slate-400">{entry.name}:</span>
              <span className="text-slate-900 dark:text-white font-semibold tabular-nums">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate team analytics
  const getTeamAnalytics = () => {
    if (!teamPerformance.length) return null;
    
    const totalTeamSales = teamPerformance.reduce((acc, m) => acc + (m.sales_count || m.records_uploaded || 0), 0);
    const avgSalesPerAgent = totalTeamSales / teamPerformance.length;
    const topPerformer = teamPerformance[0];
    const performersAboveAvg = teamPerformance.filter(m => (m.sales_count || m.records_uploaded || 0) > avgSalesPerAgent).length;
    
    return {
      totalTeamSales,
      avgSalesPerAgent: Math.round(avgSalesPerAgent),
      topPerformer,
      performersAboveAvg,
      teamSize: teamPerformance.length
    };
  };

  const teamAnalytics = getTeamAnalytics();

  // Find current user's rank - memoized
  const myRank = useMemo(() => {
    if (!teamPerformance.length || !user) return null;
    const myIndex = teamPerformance.findIndex(m => m.email === user.email);
    if (myIndex === -1) return null;
    const mySales = teamPerformance[myIndex]?.sales_count || teamPerformance[myIndex]?.records_uploaded || 0;
    const nextSales = myIndex > 0 ? (teamPerformance[myIndex - 1]?.sales_count || teamPerformance[myIndex - 1]?.records_uploaded || 0) : 0;
    return {
      rank: myIndex + 1,
      total: teamPerformance.length,
      salesNeededForNext: myIndex > 0 ? nextSales - mySales + 1 : 0,
      nextPerson: myIndex > 0 ? teamPerformance[myIndex - 1] : null
    };
  }, [teamPerformance, user]);

  // Badge system for executives - memoized (Professional Design)
  const myBadges = useMemo(() => {
    if (!myStats || !myRank) return [];
    const badges = [];
    
    // Sales milestones - Professional labels
    if (myStats.my_total_sales >= 500) badges.push({ icon: Trophy, label: 'Sales Legend', desc: '500+ total sales', color: 'from-amber-500 to-amber-600' });
    else if (myStats.my_total_sales >= 200) badges.push({ icon: Star, label: 'Sales Expert', desc: '200+ total sales', color: 'from-indigo-500 to-indigo-600' });
    else if (myStats.my_total_sales >= 100) badges.push({ icon: Award, label: 'Sales Pro', desc: '100+ total sales', color: 'from-blue-500 to-blue-600' });
    else if (myStats.my_total_sales >= 50) badges.push({ icon: TrendingUp, label: 'Rising Talent', desc: '50+ total sales', color: 'from-cyan-500 to-cyan-600' });
    
    // Daily average excellence - Professional labels
    if (myStats.my_daily_average >= 5) badges.push({ icon: Zap, label: 'High Performer', desc: '5+ sales/day avg', color: 'from-emerald-500 to-emerald-600' });
    else if (myStats.my_daily_average >= 3) badges.push({ icon: Target, label: 'Consistent', desc: '3+ sales/day avg', color: 'from-green-500 to-green-600' });
    
    // Growth champion - Professional labels
    if (myStats.my_growth_percentage > 50) badges.push({ icon: TrendingUp, label: 'Growth Leader', desc: '50%+ month-over-month', color: 'from-teal-500 to-teal-600' });
    else if (myStats.my_growth_percentage > 20) badges.push({ icon: BarChart3, label: 'Improving', desc: '20%+ growth', color: 'from-sky-500 to-sky-600' });
    
    // Rank badges - Professional labels
    if (myRank.rank === 1) badges.push({ icon: Crown, label: 'Top Performer', desc: '#1 on leaderboard', color: 'from-amber-500 to-amber-600' });
    else if (myRank.rank <= 3) badges.push({ icon: Medal, label: 'Elite', desc: 'Top 3 performer', color: 'from-slate-500 to-slate-600' });
    else if (myRank.rank <= 5) badges.push({ icon: Award, label: 'Rising Star', desc: 'Top 5 performer', color: 'from-blue-500 to-blue-600' });
    
    // Monthly performance - Professional label
    if (myStats.my_current_month_sales >= 30) badges.push({ icon: Star, label: 'Monthly MVP', desc: '30+ sales this month', color: 'from-violet-500 to-violet-600' });
    
    return badges;
  }, [myStats, myRank]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-5 h-5 text-white animate-spin" />
          </div>
          <p className="text-slate-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Executive Dashboard - Individual Sales Stats + Motivational Leaderboard
  if (user?.role === 'executive') {
    return (
      <div className="space-y-6" data-testid="executive-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              Welcome back, {user?.first_name}!
              <span className="text-2xl">👋</span>
            </h1>
            <p className="text-muted-foreground mt-1">Your personal sales performance dashboard</p>
          </div>
          <Button onClick={fetchDashboardData} variant="outline" className="gap-2 hover:bg-primary hover:text-white transition-colors">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Motivational Rank Card */}
        {myRank && (
          <Card className="border-0 shadow-xl overflow-hidden bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    {myRank.rank === 1 ? (
                      <Crown className="w-10 h-10 text-amber-400" />
                    ) : myRank.rank === 2 ? (
                      <Medal className="w-10 h-10 text-slate-300" />
                    ) : myRank.rank === 3 ? (
                      <Award className="w-10 h-10 text-orange-400" />
                    ) : (
                      <span className="text-4xl font-bold text-white">#{myRank.rank}</span>
                    )}
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-medium">Your Current Rank</p>
                    <p className="text-white text-3xl font-bold">
                      {myRank.rank === 1 ? '#1 - Top Performer' : `#${myRank.rank} of ${myRank.total}`}
                    </p>
                    {myRank.rank === 1 ? (
                      <p className="text-amber-300 text-sm mt-1 flex items-center gap-1">
                        <Star className="w-4 h-4 fill-amber-300" /> Leading the team - excellent work!
                      </p>
                    ) : (
                      <p className="text-white/70 text-sm mt-1">
                        {myRank.salesNeededForNext} more sale{myRank.salesNeededForNext !== 1 ? 's' : ''} to overtake {myRank.nextPerson?.name?.split(' ')[0]}
                      </p>
                    )}
                  </div>
                </div>
                {myRank.rank > 1 && (
                  <div className="flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm rounded-xl">
                    <TrendingUp className="w-8 h-8 text-emerald-400" />
                    <div>
                      <p className="text-white font-bold text-lg">Keep Pushing</p>
                      <p className="text-white/80 text-sm">Every sale brings you closer</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Achievements & Badges */}
        {myBadges.length > 0 && (
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                My Achievements
                <span className="ml-auto text-sm font-normal text-muted-foreground">{myBadges.length} badges earned</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3">
                {myBadges.map((badge, i) => {
                  const BadgeIcon = badge.icon;
                  return (
                    <div key={i} className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r ${badge.color} text-white shadow-lg hover:scale-105 transition-transform cursor-default`}>
                      <BadgeIcon className="w-5 h-5" />
                      <div>
                        <p className="font-bold text-sm">{badge.label}</p>
                        <p className="text-[10px] text-white/80">{badge.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Sales Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="My Total Sales"
            value={myStats?.my_total_sales}
            icon={Trophy}
            gradient="from-indigo-500 via-purple-500 to-pink-500"
            size="large"
          />
          <StatCard
            title="Last Month"
            value={myStats?.my_last_month_sales}
            icon={Calendar}
            gradient="from-slate-600 to-slate-800"
          />
          <StatCard
            title="This Month"
            value={myStats?.my_current_month_sales}
            icon={TrendingUp}
            trend={myStats?.my_growth_percentage}
            gradient="from-emerald-500 to-teal-600"
          />
          <StatCard
            title="Daily Average"
            value={myStats?.my_daily_average?.toFixed(1)}
            icon={Zap}
            gradient="from-amber-500 to-orange-600"
          />
        </div>

        {/* Sales Leaderboard for Executives */}
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 p-5">
            <h3 className="text-white font-bold text-xl flex items-center gap-2">
              <Trophy className="w-6 h-6" />
              Sales Leaderboard - Where Do You Stand?
            </h3>
            <p className="text-white/80 text-sm mt-1">
              {leaderboardMeta?.month_period && `Period: ${leaderboardMeta.month_period}`}
              {leaderboardMeta?.working_days_elapsed && ` • Working Days: ${leaderboardMeta.working_days_elapsed}/${leaderboardMeta.total_working_days}`}
            </p>
          </div>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {teamPerformance.length > 0 ? teamPerformance.slice(0, 10).map((member, index) => {
                const isMe = member.email === user?.email;
                const maxSales = Math.max(...teamPerformance.map(m => m.sales_count || m.records_uploaded || 1));
                const salesCount = member.sales_count || member.records_uploaded || 0;
                const percentage = (salesCount / maxSales) * 100;
                
                return (
                  <div
                    key={member.user_id}
                    className={`flex items-center gap-4 p-4 transition-all ${
                      isMe 
                        ? 'bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-l-4 border-indigo-500' 
                        : 'hover:bg-muted/30'
                    }`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                      index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index === 0 ? <Crown className="w-5 h-5" /> :
                       index === 1 ? <Medal className="w-5 h-5" /> :
                       index === 2 ? <Award className="w-5 h-5" /> :
                       `#${index + 1}`}
                    </div>
                    
                    {/* Avatar & Name */}
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className={`font-semibold text-white ${
                          isMe ? 'bg-gradient-to-br from-indigo-500 to-purple-600' :
                          index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                          'bg-gradient-to-br from-slate-400 to-slate-500'
                        }`}>
                          {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className={`font-semibold ${isMe ? 'text-indigo-600 dark:text-indigo-400' : 'text-foreground'}`}>
                          {member.name} {isMe && <span className="text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full ml-2">You</span>}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                      </div>
                    </div>
                    
                    {/* Sales Count */}
                    <div className="text-center">
                      <p className={`text-xl font-bold ${isMe ? 'text-indigo-600 dark:text-indigo-400' : 'text-foreground'}`}>
                        {salesCount}
                      </p>
                      <p className="text-xs text-muted-foreground">sales</p>
                    </div>
                    
                    {/* Avg per Working Day */}
                    <div className="text-center hidden sm:block">
                      <p className={`text-lg font-semibold ${isMe ? 'text-purple-600 dark:text-purple-400' : 'text-foreground'}`}>
                        {member.avg_per_working_day || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">avg/day</p>
                    </div>
                    
                    {/* Progress */}
                    <div className="hidden md:block w-24">
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            isMe ? 'bg-gradient-to-r from-indigo-500 to-purple-500' :
                            index === 0 ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                            'bg-gradient-to-r from-slate-400 to-slate-500'
                          }`}
                          style={{ width: `${Math.max(percentage, 5)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No leaderboard data available yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Performance Chart */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              My Sales Activity Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dateWiseSales}>
                  <defs>
                    <linearGradient id="colorSalesExec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="#6366f1"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorSalesExec)"
                    name="Sales"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Manager & Admin Dashboard - Team Sales Stats
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            {isAdmin() ? 'Admin' : 'Manager'} Dashboard
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Team sales performance & analytics
            {stats?.month_period && (
              <span className="ml-2 text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                {stats.month_period}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Month Selector Dropdown */}
          <div className="relative">
            <Button 
              onClick={() => setShowMonthDropdown(!showMonthDropdown)}
              variant="outline" 
              className="h-9 px-3 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">
                {availableMonths.find(m => m.year === selectedMonth?.year && m.month === selectedMonth?.month)?.label || 'Select Month'}
              </span>
              {availableMonths.find(m => m.year === selectedMonth?.year && m.month === selectedMonth?.month)?.is_current && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">NOW</span>
              )}
              <ChevronDown className="w-4 h-4" />
            </Button>
            
            {showMonthDropdown && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                {availableMonths.map((m) => (
                  <button
                    key={`${m.year}-${m.month}`}
                    onClick={() => handleMonthSelect(m)}
                    className={`w-full px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between ${
                      m.year === selectedMonth?.year && m.month === selectedMonth?.month 
                        ? 'bg-indigo-50 dark:bg-indigo-900/20' 
                        : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                        {m.label}
                        {m.is_current && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">NOW</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{m.period}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <Button onClick={fetchDashboardData} variant="outline" size="icon" className="h-9 w-9 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Sales"
          value={stats?.total_sales}
          icon={Package}
          subtitle="All time records"
          variant="indigo"
        />
        <StatCard
          title="Previous Month"
          value={stats?.previous_month_sales}
          icon={Calendar}
          subtitle="Prior period"
        />
        <StatCard
          title={stats?.is_current ? "This Month" : "Selected Month"}
          value={stats?.selected_month_sales}
          icon={TrendingUp}
          trend={stats?.growth_percentage}
          subtitle={stats?.month_label || "Selected period"}
          variant="emerald"
        />
        <StatCard
          title="Daily Average"
          value={stats?.daily_average?.toFixed(1)}
          icon={BarChart3}
          subtitle={stats?.working_days ? `${stats.working_days} working days` : "Avg per day"}
          variant="amber"
        />
        <StatCard
          title="Active Agents"
          value={stats?.active_agents}
          icon={Users}
          subtitle="Team members"
          variant="sky"
        />
      </div>

      {/* Team Analytics Overview */}
      {teamAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-200 dark:border-violet-800">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{teamAnalytics.totalTeamSales}</p>
                  <p className="text-sm text-muted-foreground">Total Team Sales</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500/10 to-cyan-500/10">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{teamAnalytics.avgSalesPerAgent}</p>
                  <p className="text-sm text-muted-foreground">Avg per Agent</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500/10 to-green-500/10">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground truncate">{teamAnalytics.topPerformer?.name?.split(' ')[0]}</p>
                  <p className="text-sm text-muted-foreground">Top Performer</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-foreground">{teamPerformance.filter(m => (m.avg_per_working_day || 0) >= 3).length}</p>
                  <p className="text-sm text-muted-foreground">High Performers</p>
                  <p className="text-[10px] text-muted-foreground/70">3+ sales/day</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Date-wise Sales Chart */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-slate-200 dark:border-slate-800">
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <TrendUp className="w-5 h-5 text-indigo-600" />
              Sales Performance Trend
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Daily sales count</p>
          </div>
          <div className="p-5">
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dateWiseSales}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} className="dark:stroke-slate-800" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    fill="url(#colorSales)"
                    stroke="#6366f1"
                    strokeWidth={2}
                    name="Sales"
                    dot={{ fill: '#6366f1', strokeWidth: 2, r: 3, stroke: '#fff' }}
                    activeDot={{ r: 5, stroke: '#6366f1', strokeWidth: 2, fill: '#fff' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Offer Distribution */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-slate-200 dark:border-slate-800">
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-600" />
              Sales Distribution by Offer
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Revenue breakdown by product</p>
          </div>
          <div className="p-5">
            <div className="h-[280px] flex items-center">
              {offerStats.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={offerStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="offer_name"
                      strokeWidth={0}
                    >
                      {offerStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      formatter={(value) => <span className="text-slate-600 dark:text-slate-400 text-sm">{value}</span>}
                      wrapperStyle={{ paddingLeft: '20px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center w-full h-full text-slate-400">
                  No offer data for selected month
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* World Class Team Performance Leaderboard */}
      <WorldClassLeaderboard 
        teamPerformance={teamPerformance}
        leaderboardMeta={leaderboardMeta}
        currentUserId={user?.id}
        showMotivation={true}
      />
    </div>
  );
};

export default DashboardPage;
